// PORTAL-SETTINGS-1 (2026-10-28) — Settings for partners and introducers.
//
// Profile details save straight away. Bank details are a REQUEST: they sit
// as "awaiting approval" until an admin approves them on the person's
// profile, and the full account number is never shown back here (last four
// only). Password uses the existing PUT /auth/change-password, which signs
// every session out, so we send them to /login afterwards.
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Building2, Clock, KeyRound, Landmark, UserRound } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import PortalShell from "@/components/portal/PortalShell";
import { useAuth } from "@/context/AuthContext";
import { useConfirmDialog } from "@/lib/useConfirmDialog";
import { isImpersonating } from "@/lib/impersonation";
import { useNoIndex } from "@/lib/useNoIndex";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROFILE_FIELDS = [
  { key: "name", label: "Full name", autoComplete: "name" },
  { key: "phone", label: "Phone", autoComplete: "tel" },
  { key: "company_name", label: "Business / trading name", autoComplete: "organization" },
  { key: "abn", label: "ABN", placeholder: "e.g. 51 824 753 556" },
  { key: "address", label: "Business address", autoComplete: "street-address", wide: true },
];

const errMsg = (err, fallback) => {
  const d = err?.response?.data?.detail;
  return typeof d === "string" ? d : fallback;
};

const fmtDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? ""
    : d.toLocaleDateString("en-AU", { day: "numeric", month: "short", year: "numeric" });
};

function Section({ icon: Icon, title, description, children }) {
  return (
    <section className="bg-white border border-[#bd9a59]/30 rounded-lg p-5 sm:p-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="w-9 h-9 rounded-full bg-[#021d49]/5 flex items-center justify-center shrink-0">
          <Icon className="w-4 h-4 text-[#021d49]" />
        </div>
        <div className="min-w-0">
          <h2 className="font-serif text-lg text-[#021d49] leading-tight">{title}</h2>
          {description && <p className="text-[13px] text-gray-500 mt-1">{description}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

function BankSummary({ bank }) {
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
      <div>
        <dt className="text-[11px] uppercase tracking-wider text-gray-500">BSB</dt>
        <dd className="text-[#021d49] mt-0.5">{bank.bsb || "—"}</dd>
      </div>
      <div>
        <dt className="text-[11px] uppercase tracking-wider text-gray-500">Account number</dt>
        <dd className="text-[#021d49] mt-0.5">
          {bank.account_number_last4 ? `•••• ${bank.account_number_last4}` : "—"}
        </dd>
      </div>
      <div>
        <dt className="text-[11px] uppercase tracking-wider text-gray-500">Account name</dt>
        <dd className="text-[#021d49] mt-0.5 break-words">{bank.account_name || "—"}</dd>
      </div>
    </dl>
  );
}

export default function PortalSettingsPage() {
  useNoIndex();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { confirm, dialog } = useConfirmDialog();
  const viewingAs = isImpersonating();
  const portalHome = user?.role === "partner" ? "/partner/dashboard" : "/introducer";
  const portalLabel = user?.role === "partner" ? "Partner Portal" : "Introducer Portal";

  const [settings, setSettings] = useState(null);
  const [profile, setProfile] = useState({});
  const [savingProfile, setSavingProfile] = useState(false);
  const [bankForm, setBankForm] = useState({ bsb: "", account_number: "", account_name: "" });
  const [showBankForm, setShowBankForm] = useState(false);
  const [savingBank, setSavingBank] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const [savingPw, setSavingPw] = useState(false);

  const applySettings = (data) => {
    setSettings(data);
    setProfile(PROFILE_FIELDS.reduce((acc, f) => ({ ...acc, [f.key]: data[f.key] || "" }), {}));
  };

  useEffect(() => {
    axios
      .get(`${API}/me/settings`)
      .then((res) => applySettings(res.data))
      .catch((err) => toast.error(errMsg(err, "Couldn't load your settings")));
  }, []);

  const profileDirty = settings && PROFILE_FIELDS.some((f) => (profile[f.key] || "") !== (settings[f.key] || ""));

  const saveProfile = async (e) => {
    e.preventDefault();
    const body = {};
    PROFILE_FIELDS.forEach((f) => {
      if ((profile[f.key] || "") !== (settings[f.key] || "")) body[f.key] = profile[f.key];
    });
    setSavingProfile(true);
    try {
      const res = await axios.patch(`${API}/me/settings`, body);
      applySettings(res.data);
      toast.success("Details saved");
    } catch (err) {
      toast.error(errMsg(err, "Couldn't save your details"));
    } finally {
      setSavingProfile(false);
    }
  };

  const submitBank = async (e) => {
    e.preventDefault();
    const ok = await confirm({
      title: "Send bank details for approval?",
      description:
        "Your payouts keep going to your current account until the Solara team confirms this change with you.",
      confirmLabel: "Send for approval",
    });
    if (!ok) return;
    setSavingBank(true);
    try {
      const res = await axios.post(`${API}/me/settings/bank-change`, bankForm);
      applySettings(res.data);
      setBankForm({ bsb: "", account_number: "", account_name: "" });
      setShowBankForm(false);
      toast.success("Sent for approval. We'll confirm with you before it takes effect.");
    } catch (err) {
      toast.error(errMsg(err, "Couldn't send your bank details"));
    } finally {
      setSavingBank(false);
    }
  };

  const withdrawBank = async () => {
    const ok = await confirm({
      title: "Withdraw this bank change?",
      description: "Your payouts will stay with your current account.",
      confirmLabel: "Withdraw",
      destructive: true,
    });
    if (!ok) return;
    try {
      const res = await axios.delete(`${API}/me/settings/bank-change`);
      applySettings(res.data);
      toast.success("Request withdrawn");
    } catch (err) {
      toast.error(errMsg(err, "Couldn't withdraw the request"));
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pw.next !== pw.confirm) {
      toast.error("New passwords don't match");
      return;
    }
    if (pw.next.length < 8 || !/[A-Za-z]/.test(pw.next) || !/\d/.test(pw.next)) {
      toast.error("Use at least 8 characters, with a letter and a number");
      return;
    }
    setSavingPw(true);
    try {
      await axios.put(`${API}/auth/change-password`, {
        current_password: pw.current,
        new_password: pw.next,
      });
      toast.success("Password changed. Please sign in again.");
      await logout();
      navigate("/login");
    } catch (err) {
      toast.error(errMsg(err, "Couldn't change your password"));
      setSavingPw(false);
    }
  };

  const rightActions = (
    <Link
      to={portalHome}
      className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-white/80 hover:text-white hover:bg-white/10 rounded-md"
      data-testid="settings-back"
    >
      <ArrowLeft className="w-4 h-4" />
      <span>Back to portal</span>
    </Link>
  );

  return (
    <PortalShell portalLabel={portalLabel} brandHref={portalHome} rightActions={rightActions}>
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <header className="pt-1">
          <h1 className="font-serif text-2xl text-[#021d49]" data-testid="portal-greeting">Settings</h1>
          <p className="text-[13px] text-gray-600 mt-1">Your details, payout account and password.</p>
        </header>
        {!settings ? (
          <div className="bg-white border border-[#bd9a59]/30 rounded-lg p-6 text-sm text-gray-500">Loading…</div>
        ) : (
          <>
            <Section icon={UserRound} title="Your details">
              <form onSubmit={saveProfile} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <Label className="text-xs text-gray-600">Login email</Label>
                  <Input value={settings.email} disabled className="mt-1" />
                  <p className="text-[11px] text-gray-400 mt-1">
                    To change the email you sign in with, contact loans@sgcap.com.au.
                  </p>
                </div>
                {PROFILE_FIELDS.map((f) => (
                  <div key={f.key} className={f.wide ? "sm:col-span-2" : ""}>
                    <Label htmlFor={`settings-${f.key}`} className="text-xs text-gray-600">{f.label}</Label>
                    <Input
                      id={`settings-${f.key}`}
                      value={profile[f.key] || ""}
                      onChange={(e) => setProfile((p) => ({ ...p, [f.key]: e.target.value }))}
                      autoComplete={f.autoComplete}
                      placeholder={f.placeholder}
                      className="mt-1"
                      data-testid={`settings-${f.key}`}
                    />
                  </div>
                ))}
                <div className="sm:col-span-2 flex justify-end">
                  <Button type="submit" disabled={!profileDirty || savingProfile} data-testid="settings-save-profile">
                    {savingProfile ? "Saving…" : "Save details"}
                  </Button>
                </div>
              </form>
            </Section>

            <Section
              icon={Landmark}
              title="Payout bank account"
              description="Where your Solara commission is paid. Changes are checked with you by the Solara team before they take effect."
            >
              {settings.bank ? (
                <BankSummary bank={settings.bank} />
              ) : (
                <p className="text-sm text-gray-500">No bank account on file yet.</p>
              )}

              {settings.pending_bank_change && (
                <div className="mt-5 rounded-md border border-[#bd9a59]/50 bg-[#bd9a59]/10 p-4" data-testid="settings-pending-bank">
                  <div className="flex items-center gap-2 text-sm font-medium text-[#021d49] mb-3">
                    <Clock className="w-4 h-4 text-[#bd9a59]" />
                    Awaiting approval
                    {settings.pending_bank_change.requested_at && (
                      <span className="font-normal text-gray-500">
                        · requested {fmtDate(settings.pending_bank_change.requested_at)}
                      </span>
                    )}
                  </div>
                  <BankSummary bank={settings.pending_bank_change} />
                  {!viewingAs && (
                    <button
                      type="button"
                      onClick={withdrawBank}
                      className="mt-3 text-xs text-gray-600 hover:text-red-700 hover:underline"
                      data-testid="settings-withdraw-bank"
                    >
                      Withdraw request
                    </button>
                  )}
                </div>
              )}

              {viewingAs ? (
                <p className="mt-4 text-xs text-gray-500">
                  Bank changes can only be requested by the account holder, not while viewing as them.
                </p>
              ) : showBankForm ? (
                <form onSubmit={submitBank} className="mt-5 pt-5 border-t border-gray-100 grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="bank-bsb" className="text-xs text-gray-600">BSB</Label>
                    <Input
                      id="bank-bsb"
                      inputMode="numeric"
                      value={bankForm.bsb}
                      onChange={(e) => setBankForm((b) => ({ ...b, bsb: e.target.value }))}
                      placeholder="062-000"
                      className="mt-1"
                      required
                      data-testid="settings-bank-bsb"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bank-number" className="text-xs text-gray-600">Account number</Label>
                    <Input
                      id="bank-number"
                      inputMode="numeric"
                      value={bankForm.account_number}
                      onChange={(e) => setBankForm((b) => ({ ...b, account_number: e.target.value }))}
                      className="mt-1"
                      required
                      data-testid="settings-bank-number"
                    />
                  </div>
                  <div>
                    <Label htmlFor="bank-name" className="text-xs text-gray-600">Account name</Label>
                    <Input
                      id="bank-name"
                      value={bankForm.account_name}
                      onChange={(e) => setBankForm((b) => ({ ...b, account_name: e.target.value }))}
                      className="mt-1"
                      required
                      data-testid="settings-bank-name"
                    />
                  </div>
                  <div className="sm:col-span-3 flex flex-wrap items-center justify-end gap-3">
                    <button
                      type="button"
                      onClick={() => setShowBankForm(false)}
                      className="text-xs text-gray-500 hover:underline"
                    >
                      Cancel
                    </button>
                    <Button type="submit" disabled={savingBank} data-testid="settings-submit-bank">
                      {savingBank ? "Sending…" : "Send for approval"}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="mt-5">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowBankForm(true)}
                    data-testid="settings-change-bank"
                  >
                    <Building2 className="w-4 h-4 mr-1.5" />
                    {settings.pending_bank_change
                      ? "Replace pending request"
                      : settings.bank
                        ? "Change bank account"
                        : "Add bank account"}
                  </Button>
                </div>
              )}
            </Section>

            <Section icon={KeyRound} title="Password" description="You'll be signed out everywhere and asked to sign in again.">
              {viewingAs ? (
                <p className="text-xs text-gray-500">Not available while viewing as this user.</p>
              ) : (
                <form onSubmit={changePassword} className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="pw-current" className="text-xs text-gray-600">Current password</Label>
                    <Input
                      id="pw-current"
                      type="password"
                      autoComplete="current-password"
                      value={pw.current}
                      onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="pw-next" className="text-xs text-gray-600">New password</Label>
                    <Input
                      id="pw-next"
                      type="password"
                      autoComplete="new-password"
                      value={pw.next}
                      onChange={(e) => setPw((p) => ({ ...p, next: e.target.value }))}
                      className="mt-1"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="pw-confirm" className="text-xs text-gray-600">Confirm new password</Label>
                    <Input
                      id="pw-confirm"
                      type="password"
                      autoComplete="new-password"
                      value={pw.confirm}
                      onChange={(e) => setPw((p) => ({ ...p, confirm: e.target.value }))}
                      className="mt-1"
                      required
                    />
                  </div>
                  <p className="sm:col-span-2 text-[11px] text-gray-400 self-center">
                    At least 8 characters, with a letter and a number.
                  </p>
                  <div className="flex justify-end">
                    <Button type="submit" disabled={savingPw} data-testid="settings-change-password">
                      {savingPw ? "Changing…" : "Change password"}
                    </Button>
                  </div>
                </form>
              )}
            </Section>
          </>
        )}
      </div>
      {dialog}
    </PortalShell>
  );
}
